import { Component, Input, ChangeDetectionStrategy, ViewEncapsulation, EventEmitter, Output, ElementRef, AfterViewInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { animate, state, style, transition, trigger } from '@angular/animations';

@Component({
  selector: 'app-html-tooltip',
  standalone: false,
  template: `
    <div class="html-tooltip" 
         [ngClass]="tooltipClass" 
         (mouseenter)="onMouseEnter()"
         (mouseleave)="onMouseLeave()"
         [@tooltipAnimation]="'visible'">
      <div #tooltipContent [innerHTML]="sanitizedContent"></div>
    </div>
  `,
  styles: [],
  animations: [
    trigger('tooltipAnimation', [
      state('void', style({
        opacity: 0,
        transform: 'scale(0.9)'
      })),
      state('visible', style({
        opacity: 1,
        transform: 'scale(1)'
      })),
      transition('void => visible', [
        animate('200ms cubic-bezier(0.23, 1, 0.32, 1)')
      ]),
      transition('visible => void', [
        animate('100ms cubic-bezier(0.23, 1, 0.32, 1)')
      ])
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class HtmlTooltipComponent implements AfterViewInit {
  @Input() tooltipClass: string = '';
  @Output() tooltipMouseEnter = new EventEmitter<void>();
  @Output() tooltipMouseLeave = new EventEmitter<void>();
  @ViewChild('tooltipContent', { static: false }) tooltipContent: ElementRef;
  
  private _content: string = '';
  
  get content(): string {
    return this._content;
  }
  
  @Input()
  set content(value: string) {
    this._content = value;
    this.sanitizedContent = this.sanitizer.bypassSecurityTrustHtml(this.processLinks(value));
    // Add event listeners after content is updated
    setTimeout(() => this.addLinkEventListeners(), 0);
  }
  
  sanitizedContent: SafeHtml;
  
  constructor(private sanitizer: DomSanitizer, private elementRef: ElementRef) {}

  ngAfterViewInit(): void {
    this.addLinkEventListeners();
  }

  onMouseEnter(): void {
    this.tooltipMouseEnter.emit();
  }

  onMouseLeave(): void {
    this.tooltipMouseLeave.emit();
  }

  private processLinks(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Find all anchor links and process them
    const links = doc.querySelectorAll('a');

    links.forEach(link => {
      const href = link.getAttribute('href');
      if (href) {
        // Store the original href as a data attribute
        link.setAttribute('data-original-href', href);
        
        // Check if it's an anchor link (starts with #)
        if (href.startsWith('#')) {
          // For anchor links, keep the href but we'll handle the click event
          link.setAttribute('data-is-anchor', 'true');
        } else {
          // For external links, change to javascript:void(0)
          link.setAttribute('href', 'javascript:void(0)');
          link.setAttribute('data-is-external', 'true');
        }
      }
    });
    
    return doc.body.innerHTML;
  }

  private addLinkEventListeners(): void {
    if (this.tooltipContent && this.tooltipContent.nativeElement) {
      const links = this.tooltipContent.nativeElement.querySelectorAll('a');
      
      links.forEach(link => {
        // Remove any existing event listeners to prevent duplicates
        link.removeEventListener('click', this.handleLinkClick);
        // Add the event listener
        link.addEventListener('click', this.handleLinkClick.bind(this));
      });
    }
  }

  private handleLinkClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    
    const link = event.target as HTMLAnchorElement;
    const originalHref = link.getAttribute('data-original-href');
    const isAnchor = link.getAttribute('data-is-anchor') === 'true';
    const isExternal = link.getAttribute('data-is-external') === 'true';
    
    if (!originalHref) {
      return;
    }
    
    // Close the tooltip first
    this.onMouseLeave();
    
    setTimeout(() => {
      if (isAnchor) {
        // Handle anchor links with smooth scrolling
        this.scrollToAnchor(originalHref);
      } else if (isExternal) {
        // Handle external links
        window.location.href = originalHref;
      }
    }, 200);
  }

  private scrollToAnchor(anchorId: string): void {
    // Remove the # from the anchor ID
    const targetId = anchorId.substring(1);
    const targetElement = document.getElementById(targetId);
    
    if (targetElement) {
      // Smooth scroll to the element
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
      
      // Optionally add a highlight effect
      this.highlightElement(targetElement);
    } else {
      // If element not found, try to scroll to the top
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }

  private highlightElement(element: HTMLElement): void {
    // Add a temporary highlight class
    const originalBackground = element.style.backgroundColor;
    element.style.backgroundColor = '#ffffcc';
    element.style.transition = 'background-color 0.3s ease';
    
    // Remove highlight after 2 seconds
    setTimeout(() => {
      element.style.backgroundColor = originalBackground;
    }, 2000);
  }
}